import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Search, Filter, Play, Pause, CalendarPlus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';

const statusBadge = (status) => {
  const map = {
    active: { label: 'نشط', cls: 'bg-emerald-100 text-emerald-700' },
    trial: { label: 'تجريبي', cls: 'bg-amber-100 text-amber-700' },
    expired: { label: 'منتهي', cls: 'bg-red-100 text-red-700' },
    suspended: { label: 'موقوف', cls: 'bg-red-100 text-red-700' },
    cancelled: { label: 'ملغي', cls: 'bg-gray-100 text-gray-700' },
  };
  const s = map[status] || { label: status || '-', cls: 'bg-gray-100 text-gray-700' };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

const SubscriptionsList = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const response = await superAdminAPI.getSubscriptions(params);
      const result = response.data?.data || response.data;
      setSubscriptions(Array.isArray(result) ? result : result.items || []);
      setTotalPages(result.totalPages || result.pages || 1);
    } catch (error) {
      toast.error('فشل في تحميل الاشتراكات');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const handleSuspend = async (sub) => {
    try {
      await superAdminAPI.suspendSubscription(sub._id);
      toast.success('تم إيقاف الاشتراك');
      fetchSubscriptions();
    } catch (error) {
      toast.error('فشل في إيقاف الاشتراك');
    }
  };

  const handleActivate = async (sub) => {
    try {
      await superAdminAPI.activateSubscription(sub._id);
      toast.success('تم تفعيل الاشتراك');
      fetchSubscriptions();
    } catch (error) {
      toast.error('فشل في تفعيل الاشتراك');
    }
  };

  const handleExtend = async (sub) => {
    const days = prompt('عدد الأيام للتمديد:');
    if (!days || isNaN(days)) return;
    try {
      await superAdminAPI.extendSubscription(sub._id, { days: Number(days) });
      toast.success(`تم تمديد الاشتراك ${days} يوم`);
      fetchSubscriptions();
    } catch (error) {
      toast.error('فشل في تمديد الاشتراك');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">الاشتراكات</h1>
          <p className="text-sm text-gray-500 mt-1">إدارة اشتراكات المختبرات</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="بحث بالاسم..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pr-11 pl-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
        >
          <option value="">كل الحالات</option>
          <option value="active">نشط</option>
          <option value="trial">تجريبي</option>
          <option value="expired">منتهي</option>
          <option value="suspended">موقوف</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-right px-6 py-3 font-medium text-gray-500">المختبر</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500">الباقة</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500">الحالة</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500 hidden lg:table-cell">رصيد AI</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500 hidden md:table-cell">البداية</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500 hidden md:table-cell">الانتهاء</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {[...Array(6)].map((_, j) => (
                      <td key={j} className={`px-6 py-4 ${j >= 3 && j <= 4 ? 'hidden md:table-cell' : ''}`}>
                        <div className="h-4 w-24 bg-gray-200 rounded" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    <CreditCard className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    لا توجد اشتراكات
                  </td>
                </tr>
              ) : (
                subscriptions.map((sub) => (
                  <motion.tr
                    key={sub._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <button
                        onClick={() => navigate(`/admin/tenants/${sub.tenantId?._id || sub.tenantId}`)}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {sub.tenantId?.name || '-'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{sub.planId?.nameAr || sub.planId?.name || '-'}</td>
                    <td className="px-6 py-4">{statusBadge(sub.status)}</td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      {(() => {
                        const limit = sub.planId?.limits?.maxAIAnalysisPerMonth;
                        const used = sub.aiUsage?.analysisCount || 0;
                        if (limit === 0 || limit === undefined) return <span className="text-gray-400 text-xs">معطل</span>;
                        if (limit === -1) return <span className="text-green-600 text-xs font-medium">∞ غير محدود ({used} مستخدم)</span>;
                        return (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[80px]">
                              <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.min(100, (used / limit) * 100)}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">{used}/{limit}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 text-gray-500 hidden md:table-cell">
                      {sub.startDate ? new Date(sub.startDate).toLocaleDateString('ar-SA-u-ca-gregory') : '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-500 hidden md:table-cell">
                      {sub.endDate ? new Date(sub.endDate).toLocaleDateString('ar-SA-u-ca-gregory') : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {sub.status === 'active' || sub.status === 'trial' ? (
                          <button
                            onClick={() => handleSuspend(sub)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                            title="إيقاف"
                          >
                            <Pause className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(sub)}
                            className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg"
                            title="تفعيل"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleExtend(sub)}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                          title="تمديد"
                        >
                          <CalendarPlus className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">صفحة {page} من {totalPages}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionsList;
